import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Rocket, Globe, Server, Download, ExternalLink, FileText, LayoutTemplate,
  Files, Sparkles, CheckCircle2, Clock, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

function blobDownload(res, filename, type) {
  const url = window.URL.createObjectURL(new Blob([res.data], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const SITE_TYPES = [
  { id: "single", label: "Single-page (static)", desc: "One scrolling page — fast & simple", Icon: FileText },
  { id: "multi", label: "Multi-page", desc: "Separate Home, About, Sermons, Events & Contact pages", Icon: Files },
];

export default function PublishingPanel() {
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customMsg, setCustomMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site");
      setSite(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load site");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const content = site?.content || {};
  const hosting = site?.hosting_mode || "hosted";
  const churchId = site?.church_id;
  const siteType = content.site_type || "single";
  const status = content.status || "draft";

  const setSiteType = async (type) => {
    if (type === siteType) return;
    setBusy(true);
    try {
      await api.put("/site", { ...content, site_type: type });
      toast.success(`Site set to ${type === "multi" ? "multi-page" : "single-page"}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update site type");
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.post("/site/publish");
      toast.success("Your website is published!");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Publish failed");
    } finally {
      setBusy(false);
    }
  };

  const fname = (ext) => `${(content.church_name || "church-site").toLowerCase().replace(/\s+/g, "-")}.${ext}`;

  const downloadZip = async () => {
    try {
      const res = await api.get("/site/export/zip", { responseType: "blob" });
      blobDownload(res, fname("zip"), "application/zip");
      toast.success("Site package (ZIP) downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    }
  };

  const downloadHtml = async () => {
    try {
      const res = await api.get("/site/export", { responseType: "blob" });
      blobDownload(res, fname("html"), "text/html");
      toast.success("Website downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Download failed");
    }
  };

  const copyLink = async () => {
    if (!churchId) return;
    const link = `${window.location.origin}/view/${churchId}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Public site link copied");
    } catch {
      toast.message("Public site link", { description: link });
    }
  };

  const submitCustom = async () => {
    if (!customMsg.trim()) {
      toast.error("Please describe what you'd like.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/tickets", { subject: "Custom site request", message: customMsg.trim(), priority: "normal" });
      toast.success("Custom site request sent! We'll follow up in Support.");
      setCustomOpen(false);
      setCustomMsg("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send request");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="py-16 grid place-items-center"><div className="h-8 w-8 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="max-w-3xl space-y-6" data-testid="publishing-panel">
      <div>
        <h2 className="font-display text-xl font-bold text-slate-900">Publishing &amp; Hosting</h2>
        <p className="text-sm text-slate-500 mt-1">Choose your site structure and publish your website.</p>
      </div>

      {/* Hosting + status */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className={`grid place-items-center h-10 w-10 rounded-lg ${hosting === "self_managed" ? "bg-blue-50 text-blue-600" : "bg-amber-50 text-amber-600"}`}>
              {hosting === "self_managed" ? <Server className="h-5 w-5" /> : <Globe className="h-5 w-5" />}
            </span>
            <div>
              <div className="text-sm font-semibold text-slate-800">{hosting === "self_managed" ? "Self-managed hosting" : "Hosted by TN Web Design"}</div>
              <div className="text-xs text-slate-500">{hosting === "self_managed" ? "You publish the files to your own host." : "We host your live site for you."}</div>
            </div>
          </div>
          {status === "published" ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700" data-testid="publish-status"><CheckCircle2 className="h-4 w-4" /> Published</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700" data-testid="publish-status"><Clock className="h-4 w-4" /> Draft</span>
          )}
        </div>
        <p className="mt-3 text-xs text-slate-400">Hosting mode is set by your TN Web Design administrator.</p>
      </div>

      {/* Site type */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3"><LayoutTemplate className="h-4 w-4" /> Site structure</h3>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="site-type-picker">
          {SITE_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={busy}
              data-testid={`site-type-${t.id}`}
              onClick={() => setSiteType(t.id)}
              className={`rounded-lg border p-4 text-left transition-colors duration-200 ${siteType === t.id ? "border-amber-500 bg-amber-50 ring-1 ring-amber-500" : "border-slate-200 hover:border-slate-300"}`}
            >
              <t.Icon className={`h-5 w-5 ${siteType === t.id ? "text-amber-600" : "text-slate-400"}`} />
              <div className="mt-2 text-sm font-semibold text-slate-800">{t.label}</div>
              <div className="text-xs text-slate-500">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Publish</h3>
        <div className="flex flex-wrap gap-2">
          <Button onClick={publish} disabled={busy} data-testid="panel-publish-btn" className="rounded-md bg-amber-600 hover:bg-amber-700 text-white">
            <Rocket className="h-4 w-4 mr-2" /> Publish website
          </Button>
          {hosting === "hosted" && churchId && (
            <a href={`/view/${churchId}`} target="_blank" rel="noreferrer" data-testid="panel-view-site" className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors duration-200">
              <ExternalLink className="h-4 w-4" /> View live site
            </a>
          )}
        </div>
        <p className="mt-2 text-xs text-slate-400">Publishes your last saved changes.</p>

        {hosting === "self_managed" && (
          <div className="mt-4 border-t border-slate-100 pt-4" data-testid="self-managed-actions">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Get your files</h4>
            <div className="flex flex-wrap gap-2">
              <Button onClick={downloadZip} data-testid="panel-download-zip" className="rounded-md bg-blue-600 hover:bg-blue-700 text-white"><Download className="h-4 w-4 mr-2" /> Download ZIP</Button>
              <Button onClick={downloadHtml} variant="outline" data-testid="panel-download-html" className="rounded-md"><Download className="h-4 w-4 mr-2" /> HTML only</Button>
              <Button onClick={copyLink} variant="outline" data-testid="panel-copy-link" className="rounded-md"><ExternalLink className="h-4 w-4 mr-2" /> Copy link</Button>
            </div>
          </div>
        )}
      </div>

      {/* Custom site request */}
      <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50/50 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-slate-800">Need something custom?</div>
              <div className="text-sm text-slate-600">Request a bespoke design and our team will build it with you.</div>
            </div>
          </div>
          <Button onClick={() => setCustomOpen(true)} variant="outline" data-testid="request-custom-btn" className="rounded-md border-amber-300 text-amber-800 hover:bg-amber-100">
            Request a custom site
          </Button>
        </div>
      </div>

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent data-testid="custom-site-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Request a custom site</DialogTitle>
            <DialogDescription>Tell us your vision — we'll open a support ticket and follow up.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label>What would you like?</Label>
            <Textarea rows={5} className="mt-1.5" value={customMsg} onChange={(e) => setCustomMsg(e.target.value)} data-testid="custom-site-message" placeholder="e.g. A multi-page site with an online giving page, a blog, and a photo gallery…" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-md" onClick={() => setCustomOpen(false)}>Cancel</Button>
            <Button onClick={submitCustom} disabled={busy} data-testid="submit-custom-btn" className="rounded-md bg-amber-600 hover:bg-amber-700 text-white"><Send className="h-4 w-4 mr-2" /> Send request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
